import React, { HTMLProps, useContext } from "react";
import CurrencyInput from "react-currency-input-field";
import { useTranslation } from "react-i18next";
import { 
    useGetNibudCarKilometersOptions,
    useGetNibudCarStatusOptions,
    useGetNibudCarTypeOptions
} from "../../../api/nibud/useGetNibudOptions";
import { DISABLED_OPTION_PLACHOLDER_ID } from "../../../config/constants";
import { IconElements } from "../../../config/icons";
import { Car, NibudCarNewOrSecondHand, PensionContext } from "../../../contexts/pension-robot/PensionContext";
import { reverseFormatNumber } from "../../../utils/parsers";
import { someAreError, someAreFetching, someAreIdle, someAreLoading } from "../../../utils/queries";
import { LoadingStateWrapper } from "../../general/LoadingStateWrapper";
import { Tooltip } from "../../general/Tooltip";
import { TooltipIndicator } from "../../general/TooltipIndicator";
import { NibudField, renderOptionsForSelect } from "./NibudModalFields";
import { nibudResponseToOptions } from "./NibudModalStepHousing";

type NibudCarQuestionType = 'type' | 'boughtStatus' | 'value' | 'distance';

export const NibudModalStepCars: React.FC<HTMLProps<HTMLDivElement>> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo }
    } = useContext(PensionContext);

    return (
        <>
            <NibudField info={
                {
                    title: t('pension-nibud-car'),
                    type: 'switch',
                    tooltip: { title: 'TODO', text: 'TODO' },
                    get: (params) => '' + params?.nibud?.carInfo?.hasCar,
                    onChange: (value) => {
                        return { expensesInfo: { nibud: { carInfo: { hasCar: value === 'yes' } } } };
                    },
                }
            }/>
            {expensesInfo?.nibud?.carInfo?.hasCar && (expensesInfo?.nibud?.carInfo?.cars ?? []).map((_, i:number) => 
                <div key={`car-${i}`} className="mb-4">
                    <NibudModalStepCarForm carId={i} />
                </div>
            )}
            {expensesInfo?.nibud?.carInfo?.hasCar && (expensesInfo?.nibud?.carInfo?.cars ?? []).length < 100 && <AddCarButton />}
        </>
    );
};

const AddCarButton: React.FC<HTMLProps<HTMLDivElement>> = ( className ) => {
    const { t } = useTranslation('pension');    
    const {
        parameters: { expensesInfo },
        actions
    } = useContext(PensionContext);

    return (
        <button
            onClick={() => {
                actions.setParameters({
                    expensesInfo: {
                        nibud: {
                            carInfo: {
                                cars: (expensesInfo?.nibud?.carInfo?.cars ?? []).concat({ 
                                    type: undefined,
                                    distance: undefined,
                                    carBoughtStatus: undefined,
                                    carValue: 0 
                                }),
                            }
                        } 
                    },
                });
            }}
            className={`${className} flex flex-row items-start ml-4 text-bnd-green`}
        >
            <span>
            {(expensesInfo?.nibud?.carInfo?.cars ?? []).length > 0 
                ? t('pension-nibud-car-add-new')
                : t('pension-nibud-car-add-first')}
            </span>
        </button>
    );
}

const NibudModalStepCarForm: React.FC<{carId: number}> = ({ carId }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo },
    } = useContext(PensionContext);
    const nibudCarTypeOptions = useGetNibudCarTypeOptions();
    const nibudCarStatusOptions = useGetNibudCarStatusOptions();
    const nibudCarKilometerOptions = useGetNibudCarKilometersOptions();

    const optionsToLoad = [nibudCarTypeOptions, nibudCarStatusOptions, nibudCarKilometerOptions]

    return (
        <LoadingStateWrapper 
            isLoading={someAreLoading(optionsToLoad)}
            isFetching={someAreFetching(optionsToLoad)}
            isError={someAreError(optionsToLoad)}
            isIdle={someAreIdle(optionsToLoad)}
            >
                <span className="mt-8 mb-8 ml-4 text-xl font-bold">{`Auto ${carId + 1}`}</span>
                <NibudCarInputContainer
                    title={t('pension-nibud-car-type')}
                    inputType="type"
                    carId={carId}
                    tooltip={{ title: 'TODO', text: 'TODO' }}
                />
                <NibudCarInputContainer
                    title={t('pension-nibud-car-bought-status')}
                    inputType="boughtStatus"
                    carId={carId}
                    tooltip={{ title: 'TODO', text: 'TODO' }}
                />
                <NibudCarInputContainer
                    title={t('pension-nibud-car-value')}
                    inputType="value"
                    carId={carId}
                    tooltip={{ title: 'TODO', text: 'TODO' }}
                />
                <NibudCarInputContainer
                    title={t('pension-nibud-car-distance')}
                    inputType="distance"
                    carId={carId}
                    tooltip={{ title: 'TODO', text: 'TODO' }}
                />
                {(expensesInfo?.nibud?.carInfo?.cars ?? []).length > 1 && <NibudModalStepRemoveCar 
                    carId={carId} 
                    className="ml-4 mb-8"
                />}
        </LoadingStateWrapper>
    );
};


const NibudCarInputContainer: React.FC<{title: string, inputType: NibudCarQuestionType, carId: number, tooltip?: { title: string, text: string}}> 
= ({ title, inputType, carId, tooltip }) => {
    return (
        <div className="mb-6 ml-4 gap-2 sm:gap-4 flex flex-row flex-wrap sm:flex-nowrap justify-between items-center">
            <div className="w-56 sm:w-64 items-center">{title}</div>
            {inputType === 'type' && <NibudCarSelectType carId={carId} />}
            {inputType === 'boughtStatus' && <NibudCarSelectBoughtStatus carId={carId} />}
            {inputType === 'value' && <NibudCarFieldValue carId={carId} />}
            {inputType === 'distance' && <NibudCarFieldDistance carId={carId} />}
            {!!tooltip && (
                <div style={{ flex: 2 }}>
                    <Tooltip
                        className="w-10"
                        tooltipId={tooltip.title}
                        tooltipTitle={tooltip.title}
                        tooltipText={tooltip.text}
                    >
                        <TooltipIndicator>?</TooltipIndicator>
                    </Tooltip>
                </div>
            )}
        </div>
    );
};

const NibudCarSelectType: React.FC<{ carId: number }> = ({ carId }) => {
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);
    const nibudCarTypeOptions = useGetNibudCarTypeOptions();
    const currentCar = (expensesInfo?.nibud?.carInfo?.cars[carId] ?? {}) as Car;

    return (
        <span className="shadow-inner relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-gray-100">
            <select
                className="w-60 sm:w-60 px-0 py-1.5 pr-10 focus:outline-none appearance-none bg-opacity-0 bg-gray-100 cursor-pointer"
                value={currentCar.type ? currentCar.type : DISABLED_OPTION_PLACHOLDER_ID}
                onChange={(e) => {
                    currentCar.type = e.target.value;
                    actions.setParameters({ 
                        expensesInfo: { 
                            nibud: {
                                carInfo: {
                                    cars: expensesInfo?.nibud?.carInfo?.cars 
                                } 
                            }
                        }
                    });
                }}
            >
                { nibudCarTypeOptions.data && renderOptionsForSelect(nibudResponseToOptions(nibudCarTypeOptions.data)) }
            </select>
            <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
        </span>
    );
};

const NibudCarSelectBoughtStatus: React.FC<{ carId: number }> = ({ carId }) => {
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);
    const nibudCarStatusOptions = useGetNibudCarStatusOptions();
    const currentCar = (expensesInfo?.nibud?.carInfo?.cars[carId] ?? {}) as Car;

    return (
        <span className="shadow-inner relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-gray-100">
            <select
                className="w-60 sm:w-60 px-0 py-1.5 pr-10 focus:outline-none appearance-none bg-opacity-0 bg-gray-100 cursor-pointer"
                value={currentCar.carBoughtStatus ? currentCar.carBoughtStatus : DISABLED_OPTION_PLACHOLDER_ID}
                onChange={(e) => {
                    currentCar.carBoughtStatus = e.target.value as NibudCarNewOrSecondHand;
                    actions.setParameters({ 
                        expensesInfo: { 
                            nibud: {
                                carInfo: {
                                    cars: expensesInfo?.nibud?.carInfo?.cars 
                                } 
                            }
                        }
                    });
                }}
            >
                { nibudCarStatusOptions.data && renderOptionsForSelect(nibudResponseToOptions(nibudCarStatusOptions.data)) }
            </select>
            <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
        </span>
    );
};

const NibudCarFieldValue: React.FC<{carId: number}> = ({ carId }) => {
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);
    const currentCar = (expensesInfo?.nibud?.carInfo?.cars[carId] ?? {}) as Car;

    return (
        <CurrencyInput
            type="text"
            className={'w-56 sm:w-64 py-2 px-4 border border-gray-200 bg-gray-100 rounded-lg focus:outline-none shadow-inner'}
            fixedDecimalLength={0}
            placeholder="€"
            decimalsLimit={2}
            intlConfig={{ currency: 'EUR', locale: 'nl-NL' }}
            allowDecimals={false}
            value={!!currentCar.carValue ? currentCar.carValue / 100 : 0}
            onValueChange={(value = '', _) => {
                currentCar.carValue = reverseFormatNumber(value, 'nl-NL') * 100;
                actions.setParameters({ 
                    expensesInfo: {
                        nibud: {
                            carInfo: {
                                cars: expensesInfo?.nibud?.carInfo?.cars
                            }
                        }
                    } 
                });
            }}
        />
    );
};

const NibudCarFieldDistance: React.FC<{carId: number}> = ({ carId }) => {
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);
    const nibudCarKilometerOptions = useGetNibudCarKilometersOptions();
    const currentCar = (expensesInfo?.nibud?.carInfo?.cars[carId] ?? {}) as Car;

    return (
        <span className="shadow-inner relative flex flex-row items-center pl-4 py-0.5 border rounded-lg bg-gray-100">
            <select
                className="w-60 px-0 py-1.5 pr-10 focus:outline-none appearance-none bg-opacity-0 bg-gray-100 cursor-pointer"
                value={currentCar.distance ? currentCar.distance : DISABLED_OPTION_PLACHOLDER_ID}
                onChange={(e) => {
                    currentCar.distance = e.target.value;
                    actions.setParameters({ 
                        expensesInfo: { 
                            nibud: {
                                carInfo: {
                                    cars: expensesInfo?.nibud?.carInfo?.cars 
                                }
                            }
                        }
                    });
                }}
            >
                { nibudCarKilometerOptions.data && renderOptionsForSelect(nibudResponseToOptions(nibudCarKilometerOptions.data)) }
            </select>
            <IconElements.ChevronDown className="absolute right-4 h-4 w-4 text-black pointer-events-none" />
        </span>
    );
}

const NibudModalStepRemoveCar: React.FC<{carId: number} & HTMLProps<HTMLDivElement>> = ({ carId, className }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: { expensesInfo },
        actions,
    } = useContext(PensionContext);

    const removeAccount = (index:number) => {
        let cars = expensesInfo?.nibud?.carInfo?.cars ?? [];
        cars.splice(index, 1);
        actions.setParameters({ expensesInfo: { nibud: { carInfo: { cars: cars } } } });
    }

    return (
        <button 
            className={`${className} text-bnd-green underline`}
            onClick={() => removeAccount(carId)}>
            {t("pension-nibud-car-remove-btn")}
        </button>
    );
};
